/* eslint-disable no-console */
import * as Sentry from "@sentry/browser";
import { IComponent, IPath, ISegment, ISegmentName, ITakeSegmentsState } from "@imaginelearning/mps-ctk-reader-wrapper";
import React, { useEffect, useState } from "react";
import Actions from "../actions/actions";
import Button from "../button/button";
import { ButtonTypeEnum, IButton } from "../button/button.props";
import Pagination from "../pagination/pagination";
import Navbar from "../../../navbar/navbar";
import PositionEnum from "../../../../enums/position.enum";
import ShadesEnum from "../../../../enums/shades.enum";
import DirectionEnum from "../../../../enums/direction.enum";
import OrientationEnum from "../../../../enums/orientation.enum";
import IFooterProps from "./footer.props";

declare global {
  interface Window {
    ContentReader: any;
    SpeechStream: any;
  }
}
window.ContentReader = window.ContentReader || {};

function hasIncompleteRequiredComponents(components: IComponent[]): boolean {
  return components.some((comp: IComponent) => comp.completionRequired && !comp.state.completed);
}

const Footer = (props: IFooterProps) => {
  const { activityPathId, isMasteryCheck, isRetryVisible, sideKickMessageCallback } = props;
  const buttonType = isMasteryCheck ? ButtonTypeEnum.SUBMIT : ButtonTypeEnum.CONTINUE;
  const [footerButton, SetFooterButton] = useState<IButton>({
    enabled: false,
    type: isRetryVisible ? ButtonTypeEnum.TRY_AGAIN : buttonType,
    callback: isRetryVisible ? window.ContentReader.closeFeedback : window.ContentReader.goToNextSegment
  });

  const retryButtonState: IButton = {
    enabled: false,
    type: ButtonTypeEnum.TRY_AGAIN,
    callback: window.ContentReader.closeFeedback
  };

  const [currentActivityPathId, setCurrentActivityPathId] = useState<number>();
  const [currentSegment, setCurrentSegment] = useState<ISegment | null>(null);
  const [playerActions, setPlayerActions] = useState({ hintId: "", vocabId: "" });
  const [segmentTitles, setSegmentTitles] = useState<ISegmentName[] | null>(null);
  const [pathDetails, setPathDetails] = useState<any>();
  const [currentPath, setCurrentPath] = useState({ pathId: "", version: "", title: "" });
  const [showSubmit, setShowSubmit] = useState(false);
  const [hideLoader, setHideLoader] = useState(false);
  const [takeSegmentsState, setTakeSegmentsState] = useState<ITakeSegmentsState>({
    segmentIds: [],
    inProgressSegmentIndex: -1
  });

  const resetLoader = () => {
    setHideLoader(false);
  };
  const updateFooterButton = (newButtonState: IButton) => {
    if (
      newButtonState.enabled !== footerButton.enabled ||
      newButtonState.type !== footerButton.type ||
      newButtonState.callback !== footerButton.callback
    ) {
      if (isRetryVisible) {
        newButtonState = retryButtonState;
        newButtonState.enabled = true;
      }

      SetFooterButton(newButtonState);
    }
  };

  const updateSegmentsState = (segmentsState: any) => {
    let inProgressSegmentIndex = segmentsState.findIndex(
      (segmentState: any) => segmentState.totalCompleted !== segmentState.totalRequired
    );
    if (inProgressSegmentIndex === -1) inProgressSegmentIndex = segmentsState.length - 1;
    const segmentIds = segmentsState.map((segmentState: any) => segmentState.segmentId);

    if (
      takeSegmentsState.inProgressSegmentIndex !== inProgressSegmentIndex ||
      currentActivityPathId !== activityPathId
    ) {
      setTakeSegmentsState({ segmentIds, inProgressSegmentIndex });
      setCurrentActivityPathId(activityPathId);
    }
  };

  // function to check if max score exceeds 1
  const maxScoreCheck = (questionData: any, id: string) => {
    if (!questionData.maxScore || questionData.maxScore <= 1) return;

    alert(`MyPath requires that items be worth 1 point - Segment ID: ${id}, Path ID: ${currentPath.pathId}`);

    Sentry.captureException(
      new Error(`MyPath requires that items be worth 1 point  - Segment ID: ${id}, Path ID: ${currentPath.pathId}`),
      (scope) => {
        scope.clear();
        scope.setTags({
          segmentId: id,
          pathId: currentPath.pathId || "empty"
        });
        return scope;
      }
    );
  };

  const FooterListener = {
    onPathLaunched: (path: IPath) => {
      if (path) {
        const buttonState = { ...footerButton };
        buttonState.enabled = true;
        setPathDetails(path);
        updateFooterButton(buttonState);
        setSegmentTitles(path.segments);
        setCurrentPath({ pathId: path.pathId, version: path.pathVersion.toString(), title: path.pathName });
      }
    },

    onSegmentStateChange: (segment: ISegment) => {
      const isFeedbackAvailable = segment.components[0]?.state?.feedbackCompleted;

      const hideLoaderForNonMc =
        !isMasteryCheck && (isFeedbackAvailable || isFeedbackAvailable === false || segment.components.length > 1);
      const hideLoaderForMc = isMasteryCheck && segment.segmentOrder.position != segment.segmentOrder.total;
      if (hideLoaderForNonMc || hideLoaderForMc) {
        setHideLoader(true); // hide footer loader
      }

      if (segment) {
        if (typeof sideKickMessageCallback === "function") {
          sideKickMessageCallback(segment, false);
          if (isRetryVisible) document.querySelectorAll(".sidekick-message")[0]?.classList.add("hide-sidekick");
        }
      }

      if (JSON.stringify(currentSegment) !== JSON.stringify(segment)) {
        if (segmentTitles) {
          const current = segmentTitles.find((x) => x.id === segment.segmentId);
          if (current) {
            segment.title = current.title;
          }
        }
        setCurrentSegment(segment);
      }

      const isLastSegment = segment.segmentOrder.position === segment.segmentOrder.total;
      let canComplete = true;
      const buttonState = {
        enabled: true,
        type: isMasteryCheck ? ButtonTypeEnum.SUBMIT : ButtonTypeEnum.CONTINUE,
        callback: window.ContentReader.goToNextSegment
      };

      const questionComps = segment.components.filter(
        (comp: IComponent) => comp.type === "Question" || comp.type === "QuestionItem"
      );

      if (questionComps.length) {
        // check if max score excceds 1
        if (isMasteryCheck) maxScoreCheck(questionComps[0], segment.segmentId);

        const allQuestionCompsComplete = !questionComps.some((comp: IComponent) => !comp.state.completed);

        if (!allQuestionCompsComplete) {
          if (questionComps.length === 1) {
            buttonState.type = ButtonTypeEnum.SUBMIT;
            setShowSubmit(false);
          } else {
            setShowSubmit(true);
          }

          buttonState.callback = function handleActivitySubmitClick() {
            buttonState.enabled = false;
            SetFooterButton(buttonState);
            window.ContentReader.checkAnswers();
          };
          canComplete = false;

          const shouldDisableSubmitButton = questionComps.some((comp: IComponent) => {
            const hasOnlyOneComp: boolean = questionComps.length === 1;
            const hasIncompleteComp = questionComps.length > 1 && !comp.state.completed;
            const isInvalid: boolean = !comp.state.valid;

            return (hasOnlyOneComp && isInvalid) || hasIncompleteComp;
          });

          if (shouldDisableSubmitButton) {
            buttonState.enabled = false;
          }
        }

        if (isMasteryCheck && allQuestionCompsComplete) {
          if (isLastSegment) {
            canComplete = false;
            if (!segment.takeSubmitted) {
              //Cannot submit take as a teacher
            }
          } else {
            buttonState.enabled = false;
            SetFooterButton(buttonState);
            window.ContentReader.goToNextSegment();
          }
        }

        const hasAttempts = questionComps[0]?.state?.attempts !== 0;
        const feedbackNotCompleted = hasAttempts && !questionComps[0]?.state?.feedbackCompleted;

        if (!isMasteryCheck && feedbackNotCompleted) {
          buttonState.enabled = false;
        }
      } else if (hasIncompleteRequiredComponents(segment.components)) {
        buttonState.enabled = false;
        canComplete = false;
      }

      if (canComplete && isLastSegment) {
        buttonState.callback = function handleActivityComplete() {}; //submitting a take as a teacher causes an error, do nothing instead
      }

      // If the button state is still enabled, user most likely answered the question, close sidekick message if it's open
      if (buttonState.enabled) {
        if (
          typeof sideKickMessageCallback === "function" &&
          document.querySelectorAll(".sidekick-message")?.length > 0
        ) {
          sideKickMessageCallback(segment, true);
          if (isRetryVisible) {
            buttonState.type = retryButtonState.type;
            buttonState.callback = retryButtonState.callback;
          }
        }
      }
      updateFooterButton(buttonState);
      window.document.dispatchEvent(new Event("keydown")); // for idle time - keep active as long as there is activity in the player
    },

    onSegmentNavigationChange: (data: any) => {
      setPlayerActions({ hintId: "", vocabId: "" });
      if (data && data.panelsInSegment && data.panelsInSegment.length > 0) {
        let vocabId = "";
        let hintId = "";
        data.panelsInSegment.forEach((panel: any) => {
          switch (panel.icon) {
            case "book":
              vocabId = panel.id;
              break;
            case "question":
              hintId = panel.id;
              break;
            default:
              break;
          }
        });
        setPlayerActions({ hintId, vocabId });
      }
    },

    onTakeSubmitComplete: (take: any) => {
      setHideLoader(true); // mastery check last question scenario
    },

    onTakeStateChange: (take: any) => {
      if (!take.takeSubmitted) {
        updateSegmentsState(take.totalsBySegment);
      }
    },

    onSegmentReady: (data: any) => {
      const segment = pathDetails?.segments.find((seg: any) => seg.id === data.segmentId);

      if (currentSegment) {
        console.log(
          `Path ID:  ${currentPath.pathId || "empty"} Segment Title: ${currentSegment.title || "empty"} Segment ID: ${
            currentSegment.segmentId || "empty"
          }`
        );
      }

      if (showSubmit) {
        const submitDiv = document.getElementsByClassName("wnr-ccquestion-question-submit-container");
        if (submitDiv && submitDiv.length > 0) {
          for (let i: number = 0; i < submitDiv.length; i += 1) {
            const submitEle = submitDiv[i] as HTMLElement;
            if (submitEle) submitEle.style.display = "flex";
          }
        }
      }

      const videoElement = document.querySelector("video.vjs-tech");
      const parentLayout = document.getElementsByClassName("wnr-contentLayout-layout-type-video")[0];
      const feedbackVideo = document.querySelector("wnr-ccquestion-distractor-rationale-title");
      if (videoElement && !parentLayout && !feedbackVideo) {
        const segmentId = currentSegment?.segmentId || "";
        Sentry.captureException(
          new Error(
            `Content authoring missed a Layout:Video for instructional videos. Activity title: ${currentPath.title}`
          ),
          (scope) => {
            scope.clear();
            scope.setTags({
              pathId: currentPath.pathId,
              version: currentPath.version,
              segmentId
            });
            return scope;
          }
        );
      }

      // loader off
      setHideLoader(true);
    }
  };

  window.ContentReader = { ...window.ContentReader, ...FooterListener };
  const footerChildren = [
    {
      item: (
        <>
          <Actions
            show={{
              Vocab: playerActions.vocabId !== "",
              Hint: playerActions.hintId !== ""
            }}
            vocabCallback={() => {
              window.ContentReader.togglePanel(playerActions.vocabId);
            }}
            hintCallback={() => {
              window.ContentReader.togglePanel(playerActions.hintId);
            }}
          />
        </>
      ),
      direction: DirectionEnum.START
    },
    {
      item: (
        <Pagination
          count={currentSegment?.segmentOrder.total || 0}
          active={currentSegment?.segmentOrder.position || 0}
          takeSegmentsState={takeSegmentsState}
          isMasteryCheck={isMasteryCheck || false}
          goToSegmentCallback={window.ContentReader.goToSegment}
          previewMode={true}
        />
      ),
      direction: DirectionEnum.CENTER
    },
    {
      item: (
        <Button
          enabled={footerButton.enabled}
          type={footerButton.type}
          callback={footerButton.callback}
          hideLoader={hideLoader}
          resetLoader={resetLoader}
        />
      )
    }
  ];

  return (
    <Navbar
      id="content-footer"
      color={ShadesEnum.LIGHT}
      variant={ShadesEnum.LIGHT}
      orientation={OrientationEnum.HORIZONTAL}
      items={footerChildren}
      fixed={PositionEnum.BOTTOM}
      addColumnClass={true}
      className="footer"
    />
  );
};

export default Footer;
